import * as jose from 'jose'
import { HttpService, type HttpOptions } from '~/services/http/http'
import { useAuthStore } from '~/stores/auth'

export interface ApiServiceOptions extends HttpOptions {
  useFallbackAuth?: boolean
  unauthenticated?: boolean
}

export class ApiService {
  private readonly httpService: HttpService
  private readonly authStore = useAuthStore()
  private readonly config = useRuntimeConfig()

  constructor() {
    this.httpService = new HttpService(this.config.public.serverUrl, {})
  }

  async getFallbackAuthToken() {
    const secret = new TextEncoder().encode(
      this.config.public.mimiclyInterimSecretKey,
    )

    const token = await new jose.SignJWT({
      accessToken: this.config.public.mimiclyInterimAccessToken,
    })
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime('1h')
      .sign(secret)

    return token
  }

  async getHeaders(options?: Partial<ApiServiceOptions>) {
    const { headers = {} } = options || {}

    if (options?.unauthenticated) {
      return headers
    }

    if (options?.useFallbackAuth) {
      headers.fallbackAuthToken = await this.getFallbackAuthToken()
      return headers
    }

    const bearerToken = await this.authStore.getToken()

    if (!bearerToken) {
      throw new Error('Could not get bearer token')
    }

    headers.Authorization = `Bearer ${bearerToken}`

    return headers
  }

  async get<ResponseT = unknown>(
    url: string,
    options?: Partial<ApiServiceOptions>,
  ) {
    const response = await this.httpService.request<ResponseT>(url, {
      ...options,
      method: 'GET',
      headers: await this.getHeaders(options),
    })

    return response
  }

  async post<ResponseT = unknown, BodyT = unknown>(
    url: string,
    body: BodyT,
    options?: Partial<ApiServiceOptions>,
  ) {
    const response = await this.httpService.request<ResponseT>(url, {
      ...options,
      method: 'POST',
      data: body,
      headers: await this.getHeaders(options),
    })

    return response
  }

  async put<ResponseT = unknown, BodyT = unknown>(
    url: string,
    body: BodyT,
    options?: Partial<ApiServiceOptions>,
  ) {
    const response = await this.httpService.request<ResponseT>(url, {
      ...options,
      method: 'PUT',
      data: body,
      headers: await this.getHeaders(options),
    })

    return response
  }

  async patch<ResponseT = unknown, BodyT = unknown>(
    url: string,
    body: BodyT,
    options?: Partial<ApiServiceOptions>,
  ) {
    const response = await this.httpService.request<ResponseT>(url, {
      ...options,
      method: 'PATCH',
      data: body,
      headers: await this.getHeaders(options),
    })

    return response
  }

  async delete<ResponseT = unknown>(
    url: string,
    options?: Partial<ApiServiceOptions>,
  ) {
    const response = await this.httpService.request<ResponseT>(url, {
      ...options,
      method: 'DELETE',
      headers: await this.getHeaders(options),
    })

    return response
  }
}
